import { useCallback } from 'react';
import { useConfig, useDispatch, useMachineState } from '../app/context';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { cx } from '../components/cx';
import { HazardFrame } from '../components/HazardFrame';
import { StopIcon } from '../components/icons';
import { ProgressRing } from '../components/ProgressRing';
import { bulbSummary } from '../domain/catalog';
import { formatDuration, targetLabel } from '../domain/format';
import common from './common.module.css';
import styles from './CycleScreen.module.css';

export function CycleScreen() {
  const {
    ward,
    bed,
    position,
    operator,
    cycleRemaining,
    cycleTotal,
    estopPending,
    channels,
    lampChannels,
  } = useMachineState();
  const { demoMode } = useConfig();
  const dispatch = useDispatch();
  const cancelStop = useCallback(() => dispatch({ type: 'EMERGENCY_STOP_CANCEL' }), [dispatch]);

  const elapsedFraction = cycleTotal > 0 ? (cycleTotal - cycleRemaining) / cycleTotal : 1;
  const pct = Math.min(100, Math.round(elapsedFraction * 100));
  const timeLeft = formatDuration(Math.ceil(cycleRemaining));

  return (
    <section
      className={cx(common.screen, styles.screen)}
      aria-label="Disinfection cycle in progress"
    >
      <HazardFrame tone="danger" />

      <div className={cx(common.card, styles.details)}>
        <div className={common.label}>ROOM DETAILS</div>
        <dl className={styles.fields}>
          <div>
            <dt className={common.fieldLabel}>TARGET</dt>
            <dd>{ward && bed ? targetLabel(ward.code, bed) : '—'}</dd>
          </div>
          <div>
            <dt className={common.fieldLabel}>ROBOT POSITION</dt>
            <dd>{position?.label ?? '—'}</dd>
          </div>
          <div>
            <dt className={common.fieldLabel}>BULBS ON</dt>
            <dd>{bulbSummary(channels, lampChannels)}</dd>
          </div>
          <div>
            {/* No occupancy sensor is fitted: say what the clearance actually rests on. */}
            <dt className={common.fieldLabel}>Room clearance</dt>
            <dd className={styles.safeBadge}>
              <span className={styles.safeDot} />
              CONFIRMED BY OPERATOR
            </dd>
          </div>
          <div>
            <dt className={common.fieldLabel}>OPERATOR</dt>
            <dd>{operator?.name.toUpperCase() ?? '—'}</dd>
          </div>
        </dl>
      </div>

      <div className={styles.center}>
        <div className={styles.danger} role="alert">
          DANGER: LIGHT IS ON.
          <br />
          DO NOT ENTER ROOM!
        </div>
        <ProgressRing
          size={400}
          radius={176}
          strokeWidth={10}
          fraction={elapsedFraction}
          tone="danger"
          contentClassName={styles.ringContent}
        >
          <div className={common.fieldLabel}>TIME LEFT</div>
          <div className={styles.time} role="timer" aria-label={`${timeLeft} remaining`}>
            {timeLeft}
          </div>
          <div className={styles.percent}>{pct}% DONE</div>
        </ProgressRing>
      </div>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.estop}
          onClick={() => dispatch({ type: 'EMERGENCY_STOP' })}
        >
          <StopIcon size={32} />
          EMERGENCY STOP
        </button>
        <div className={styles.estopHint}>Tap to stop the machine immediately.</div>
        {demoMode && (
          <button
            type="button"
            className={styles.demoBreach}
            onClick={() => dispatch({ type: 'OCCUPANCY_BREACH' })}
          >
            DEMO · SIMULATE MOTION BREACH
          </button>
        )}
      </div>

      {estopPending && (
        <ConfirmDialog
          title="Confirm Emergency Stop"
          body="The lamp array will shut down immediately. The room will NOT be sanitised and the cycle must restart from the beginning."
          cancelLabel="RESUME MONITORING"
          confirmLabel="CONFIRM STOP"
          onCancel={cancelStop}
          onConfirm={() => dispatch({ type: 'EMERGENCY_STOP_CONFIRM' })}
        />
      )}
    </section>
  );
}
